<template>
  <BaseModule
    v-bind="$props"
    class="support-header"
    data-module="support-header"
  >
    <header class="support-header__header">
      <div class="support-header__container">
        <div class="support-header__logo">
          <EditorLink :model="linkUrl">
            <img
              v-if="!textInsteadOfLogo"
              :src="logoHandler.src"
              :alt="logoHandler.alt"
            />
            <component
              :is="titleTag"
              v-else
              class="tit tit-g"
            >
              {{ textAsLogo }}
            </component>
          </EditorLink>
        </div>

        <nav class="support-header__menu">
          <ul class="support-header__list">
            <li
              v-for="item in menuList"
              :key="item.id"
            >
              <EditorLink :model="item.itemUrl">
                {{ item.itemText }}
              </EditorLink>
            </li>
          </ul>
        </nav>

        <div
          v-if="translations.length > 1"
          class="support-header__languages"
        >
          <EditorLink
            v-for="({ label, value, language }, index) in translations"
            :key="index"
            :model="value"
            class="language-item"
            :class="{ 'language-item--active': isActiveLanguage(language) }"
          >
            {{ label }}
          </EditorLink>
        </div>
      </div>
    </header>
  </BaseModule>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import debounce from 'lodash/debounce';

import base from '@/mixins/base';
import BaseModule from './generic/BaseModule.vue';
import EditorLink from './EditorLink.vue';

export default {
  name: 'SupportHeader',
  components: { BaseModule, EditorLink },
  mixins: [base],
  props: {
    ...BaseModule.props,
    image: {
      type: String,
      default: '',
    },
    imageMobile: {
      type: String,
      default: '',
    },
    altText: {
      type: String,
      default: '',
    },
    altTextMobile: {
      type: String,
      default: '',
    },
    linkUrl: {
      type: Object,
      default: () => ({
        type: 'externalLink',
        value: '',
        openInNewTab: true,
      }),
    },
    haveMobileImage: {
      type: Boolean,
      default: false,
    },
    textInsteadOfLogo: {
      type: Boolean,
      default: false,
    },
    textAsLogo: {
      type: String,
      default: '',
    },
    menuList: {
      type: Array,
      default() {
        return [
          {
            id: 'DefaultUneditedId',
            itemText: 'Lorem Ipsum',
            itemUrl: {
              type: 'externalLink',
              value: '',
              openInNewTab: true,
            },
          },
        ];
      },
    },
  },
  data() {
    return {
      isMobile: window.innerWidth < 768,
    };
  },
  computed: {
    logoHandler() {
      if (this.haveMobileImage && this.isMobile) {
        return { src: this.imageMobile, alt: this.altTextMobile };
      }

      return { src: this.image, alt: this.altText };
    },
    translations() {
      const langs = {
        pt: 'PT',
        en: 'EN',
        es: 'ES',
      };

      return this.activePage.translationIds.map((id) => ({
        label: langs[this.pages[id].language],
        language: this.pages[id].language,
        value: {
          openInNewTab: false,
          type: 'page',
          value: this.pages[id].id,
        },
      }));
    },
    ...mapState(['pages']),
    ...mapGetters(['activePage']),
  },
  mounted() {
    window.addEventListener('resize', debounce(this.onResize, 150));
  },
  methods: {
    onResize() {
      const isMobile = window.innerWidth < 768;

      if (this.isMobile !== isMobile) {
        this.isMobile = isMobile;
      }
    },
    isActiveLanguage(language) {
      return this.$route.params.language === language;
    },
  },
  metadata: {
    extends: BaseModule.metadata,
    tabs: [
      {
        name: 'content',
        groups: [
          {
            name: 'logo',
            label: 'Logo',
            fields: [
              {
                name: 'textInsteadOfLogo',
                type: 'Checkbox',
                label: 'Usar texto ao invés de logo',
              },
              {
                name: 'textAsLogo',
                type: 'ShortText',
                label: 'Texto',
                isEnabled({ textInsteadOfLogo }) {
                  return textInsteadOfLogo;
                },
              },
              {
                name: 'image',
                type: 'ImageFile',
                label: 'Logo',
                helpText: 'Tamanho máximo 200x24, formato .PNG',
                isEnabled({ textInsteadOfLogo }) {
                  return !textInsteadOfLogo;
                },
              },
              {
                name: 'altText',
                type: 'ShortText',
                label: 'Texto alternativo',
                isEnabled({ textInsteadOfLogo }) {
                  return !textInsteadOfLogo;
                },
              },
              {
                name: 'haveMobileImage',
                type: 'Checkbox',
                label: 'Usar uma imagem diferente para mobile',
                isEnabled({ textInsteadOfLogo }) {
                  return !textInsteadOfLogo;
                },
              },
              {
                name: 'imageMobile',
                type: 'ImageFile',
                label: 'Logo mobile',
                helpText: 'Tamanho máximo 80x24, formato .PNG',
                isEnabled({ haveMobileImage, textInsteadOfLogo }) {
                  return haveMobileImage && !textInsteadOfLogo;
                },
              },
              {
                name: 'altTextMobile',
                type: 'ShortText',
                label: 'Texto alternativo mobile',
                isEnabled({ haveMobileImage, textInsteadOfLogo }) {
                  return haveMobileImage && !textInsteadOfLogo;
                },
              },
              {
                name: 'linkUrl',
                type: 'Url',
                label: 'Link/URL (opcional)',
              },
            ],
          },
          {
            name: 'menu',
            label: 'Menu',
            fields: [
              {
                name: 'menuList',
                type: 'List',
                label: 'Menu',
                addItemLabel: 'Adicionar item',
                fields: [
                  {
                    name: 'itemText',
                    type: 'ShortText',
                    label: 'Texto do botão',
                    maxlength: 30,
                  },
                  {
                    name: 'itemUrl',
                    type: 'Url',
                  },
                ],
              },
            ],
          },
        ],
        toRemove: ['text', 'buttonText'],
      },
      {
        name: 'appearance',
        groups: [
          {
            name: 'colors',
            fields: [
              {
                name: 'titleColor',
                isEnabled({ textInsteadOfLogo }) {
                  return textInsteadOfLogo;
                },
              },
              {
                name: 'textColor',
                label: 'Cor do menu',
              },
            ],
            toRemove: ['buttonTextColor', 'buttonColor'],
          },
          {
            name: 'fonts',
            label: 'Fontes',
            fields: [
              {
                name: 'titleSizeDesk',
                isEnabled({ textInsteadOfLogo }) {
                  return textInsteadOfLogo;
                },
              },
              {
                name: 'titleSizeMobile',
                isEnabled({ textInsteadOfLogo }) {
                  return textInsteadOfLogo;
                },
              },
              {
                name: 'contentSizeDesk',
                label: 'Menu desktop',
              },
              {
                name: 'contentSizeMobile',
                label: 'Menu mobile',
              },
            ],
          },
        ],
        toRemove: ['transitions'],
      },
    ],
  },
};
</script>

<style scoped lang="scss">
.support-header {
  &__header {
    display: flex;
    min-height: 40px;
    margin: 0;
    padding-top: var(--padding-top);
    padding-bottom: var(--padding-bottom);
  }

  &__container {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    margin: 0 auto;
  }

  &__logo {
    margin: 0 16px;

    .editor-link {
      display: flex;
      text-decoration: none;
    }

    h1,
    h2 {
      margin: 0;
      padding: 0;
      font-size: var(--title-size-desk);
    }

    img {
      display: block;
      max-width: 200px;
      max-height: 30px;
    }
  }

  &__menu {
    transition: 0.4s;
    margin-left: auto;
    overflow: hidden;
  }

  &__list::-webkit-scrollbar {
    display: none; /* esconde scrollbar no Chrome */
  }

  &__list {
    overflow-x: scroll;
    display: flex;
    font-size: var(--content-size-desk);
    line-height: 1.2;
    -ms-overflow-style: none; /* esconde scrollbar no IE e Edge */
    scrollbar-width: none; /* esconde scrollbar no Firefox */

    li {
      width: unset;
      text-align: center;

      .editor-link {
        color: var(--text-color);
        text-decoration: none;
        white-space: nowrap;
        margin-right: 1em;
      }
    }
  }

  &__languages {
    display: flex;
    margin: 0 16px;

    .language-item {
      font-size: 14px;
      line-height: 1.2;
      color: $gray-90;

      &--active {
        font-weight: bold;
        border-bottom: 1px solid;
        cursor: default;
      }

      & + .language-item {
        margin-left: 16px;
      }
    }
  }

  @include media-breakpoint-down(md) {
    .support-header {
      &__header {
        padding-top: var(--padding-top-mobile);
        padding-bottom: var(--padding-bottom-mobile);
      }

      &__logo {
        h2 {
          font-size: var(--title-size-mobile);
        }

        img {
          max-width: 80px;
          max-height: 24px;
        }
      }

      &__list {
        font-size: var(--content-size-mobile);
      }

      &__languages {
        .language-item + .language-item {
          margin-left: 8px;
        }
      }
    }
  }
}
</style>
